import { useSession } from "@clerk/clerk-react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "@heroui/react";
import { ListChecks, Plus } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { API_URL } from "constants/index";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { CustomerInvoice } from "shared/types/customer-invoice";
import {
  PaymentPlan,
  PaymentPlanFrequency,
} from "shared/types/payment-plan";
import { applyMask } from "shared/utils/masks";
import { CreatePaymentPlanForm } from "./CreatePaymentPlanForm";
import { PaymentPlanDetail } from "./PaymentPlanDetail";

interface PaymentPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: CustomerInvoice | null;
  onPaymentPlansChanged?: () => void;
}

export const PaymentPlanModal = ({
  isOpen,
  onClose,
  invoice,
  onPaymentPlansChanged,
}: PaymentPlanModalProps) => {
  const { session } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [plans, setPlans] = useState<PaymentPlan[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeContractId, setActiveContractId] = useState<number | null>(null);

  const partnerId = invoice?.partner_id?.[0];
  const unitId = invoice?.unit?.id;

  // Fetch existing plans and active contract when modal opens
  useEffect(() => {
    if (isOpen && partnerId) {
      fetchPlans(partnerId);
      if (unitId) {
        fetchActiveContract(unitId);
      }
    } else if (!isOpen) {
      setPlans([]);
      setShowAddForm(false);
      setActiveContractId(null);
    }
  }, [isOpen, partnerId, unitId]);

  const fetchActiveContract = async (unitId: string) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/unit/${unitId}/active`,
        { method: "GET" },
        session
      );

      if (response.ok) {
        const contracts = await response.json();
        if (contracts.length > 0) {
          setActiveContractId(contracts[0].id);
        }
      }
    } catch (error) {
      console.error("Error fetching active contract:", error);
    }
  };

  const fetchPlans = async (partnerId: number) => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/payment-plans/partner/${partnerId}`,
        { method: "GET" },
        session
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      setPlans(data);
    } catch (error) {
      console.error("Error fetching payment plans:", error);
      toast.error("Fout bij het ophalen van afbetalingsplannen");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePlan = useCallback(
    async (data: {
      totalPlanAmount: number;
      numberOfInstallments: number;
      installmentAmount: number;
      frequency: PaymentPlanFrequency;
      startDate: string;
      remarks?: string;
    }) => {
      if (!partnerId) return;

      if (!activeContractId) {
        toast.error("Geen actief huurcontract gevonden voor deze unit");
        return;
      }

      setIsSaving(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/payment-plans`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...data,
              rentalContractId: activeContractId,
              odooPartnerId: partnerId,
              contactId: invoice?.contact?.id,
              companyId: invoice?.company?.id,
              totalOutstandingAmount: invoice?.amount_residual || 0,
            }),
          },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to create payment plan: ${response.status}`);
        }

        const newPlan = await response.json();
        setPlans((prev) => [newPlan, ...prev]);
        setShowAddForm(false);
        toast.success("Afbetalingsplan aangemaakt");
        onPaymentPlansChanged?.();
      } catch (error) {
        console.error("Error creating payment plan:", error);
        toast.error("Fout bij het aanmaken van afbetalingsplan");
      } finally {
        setIsSaving(false);
      }
    },
    [partnerId, activeContractId, invoice, session, onPaymentPlansChanged]
  );

  const handlePlanUpdated = useCallback(
    (updated: PaymentPlan) => {
      setPlans((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      onPaymentPlansChanged?.();
    },
    [onPaymentPlansChanged]
  );

  const handlePlanDeleted = useCallback(
    (planId: number) => {
      setPlans((prev) => prev.filter((p) => p.id !== planId));
      onPaymentPlansChanged?.();
    },
    [onPaymentPlansChanged]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <ListChecks
              weight="duotone"
              size={24}
              className="text-moss-primary"
            />
            <span>
              Afbetalingsplannen - {invoice?.invoice_partner_display_name}
            </span>
          </div>
          {invoice?.amount_residual && (
            <p className="text-xs text-neutral-secondary-mid font-normal">
              Openstand: {applyMask(invoice.amount_residual, "valuta")}
            </p>
          )}
        </ModalHeader>

        <ModalBody>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner label="Afbetalingsplannen ophalen..." color="success" />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {/* Add new plan form */}
              {showAddForm ? (
                <CreatePaymentPlanForm
                  openstandAmount={invoice?.amount_residual || 0}
                  onSubmit={handleCreatePlan}
                  onCancel={() => setShowAddForm(false)}
                  isSaving={isSaving}
                />
              ) : (
                <ButtonNew
                  variant="secondaryMoss"
                  icon={<Plus size={14} />}
                  label="Nieuw afbetalingsplan"
                  onClick={() => setShowAddForm(true)}
                />
              )}

              {/* Plans list */}
              <div className="flex flex-col gap-2">
                <h4 className="text-sm font-medium text-neutral-secondary-mid">
                  Afbetalingsplannen ({plans.length})
                </h4>

                {plans.length === 0 ? (
                  <p className="text-sm text-neutral-secondary-mid italic py-4 text-center">
                    Nog geen afbetalingsplannen
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {plans.map((plan) => (
                      <PaymentPlanDetail
                        key={plan.id}
                        plan={plan}
                        onPlanUpdated={handlePlanUpdated}
                        onPlanDeleted={handlePlanDeleted}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <ButtonNew
            variant="secondaryNeutral"
            label="Sluiten"
            onClick={onClose}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PaymentPlanModal;
